import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as authService from '../../services/authService';

function normalizarLista(resposta, idKey) {
  const bruto = resposta?.data ?? resposta;
  const lista = Array.isArray(bruto) ? bruto : bruto?.rows ?? bruto?.data ?? [];
  if (!Array.isArray(lista)) return [];
  return lista.map((item) => ({
    ...item,
    empresaId: item.empresaId ?? item.id,
    filialId: item.filialId ?? item.id,
    nome: item.nome ?? item.razaoSocial ?? item.descricao ?? String(item[idKey] ?? ''),
  }));
}

/**
 * Seletor de empresa/filial na barra — troca contexto + limpa estado de domínio.
 */
const SeletorContexto = () => {
  const navigate = useNavigate();
  const {
    empresaId,
    filialId,
    selecionarContexto,
    logout,
    usuario,
  } = useAuth();

  const [empresas, setEmpresas] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(empresaId || '');
  const [filialSelecionada, setFilialSelecionada] = useState(filialId || '');
  const [trocando, setTrocando] = useState(false);
  const [ancora, setAncora] = useState(null);
  const theme = useTheme();
  const telaPequena = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setEmpresaSelecionada(empresaId || '');
    setFilialSelecionada(filialId || '');
  }, [empresaId, filialId]);

  useEffect(() => {
    let ativo = true;
    authService
      .listarEmpresas()
      .then((resposta) => {
        if (ativo) setEmpresas(normalizarLista(resposta, 'empresaId'));
      })
      .catch(() => {
        if (ativo) setEmpresas([]);
      });
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    if (!empresaSelecionada) return undefined;
    let ativo = true;
    authService
      .listarFiliais(empresaSelecionada)
      .then((resposta) => {
        if (ativo) setFiliais(normalizarLista(resposta, 'filialId'));
      })
      .catch(() => {
        if (ativo) setFiliais([]);
      });
    return () => {
      ativo = false;
    };
  }, [empresaSelecionada]);

  const [erroTroca, setErroTroca] = useState('');

  const handleAplicar = async () => {
    if (!empresaSelecionada || !filialSelecionada) return;
    if (
      String(empresaSelecionada) === String(empresaId) &&
      String(filialSelecionada) === String(filialId)
    ) {
      return;
    }
    setTrocando(true);
    try {
      await selecionarContexto({
        empresaId: empresaSelecionada,
        filialId: filialSelecionada,
      });
      navigate('/', { replace: true });
    } catch (erro) {
      // O3: mensagem na tela (antes um alert() que travava a página)
      setErroTroca(erro?.response?.data?.message || 'Falha ao trocar empresa/filial.');
    } finally {
      setTrocando(false);
    }
  };

  // Evita warning MUI out-of-range enquanto as opções ainda não carregaram
  const valorEmpresaSelect = empresas.some(
    (empresa) => String(empresa.empresaId) === String(empresaSelecionada)
  )
    ? empresaSelecionada
    : '';
  const valorFilialSelect = filiais.some(
    (filial) => String(filial.filialId) === String(filialSelecionada)
  )
    ? filialSelecionada
    : '';

  const sair = () => {
    logout();
    navigate('/login');
  };

  const controles = (
    <>
      <Snackbar open={Boolean(erroTroca)} autoHideDuration={15000} onClose={() => setErroTroca('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" variant="filled" onClose={() => setErroTroca('')}>
          {erroTroca}
        </Alert>
      </Snackbar>
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel id="sel-empresa">Empresa</InputLabel>
        <Select
          labelId="sel-empresa"
          label="Empresa"
          value={valorEmpresaSelect}
          onChange={(e) => setEmpresaSelecionada(e.target.value)}
          displayEmpty
        >
          {empresas.length === 0 && <MenuItem value=""> </MenuItem>}
          {empresas.map((empresa) => (
            <MenuItem key={empresa.empresaId} value={String(empresa.empresaId)}>
              {empresa.empresaId}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 100 }}>
        <InputLabel id="sel-filial">Filial</InputLabel>
        <Select
          labelId="sel-filial"
          label="Filial"
          value={valorFilialSelect}
          onChange={(e) => setFilialSelecionada(e.target.value)}
          displayEmpty
        >
          {filiais.length === 0 && <MenuItem value=""> </MenuItem>}
          {filiais.map((filial) => (
            <MenuItem key={filial.filialId} value={String(filial.filialId)}>
              {filial.filialId}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        disabled={trocando}
        onClick={handleAplicar}
      >
        Trocar
      </Button>
      <Button size="small" color="inherit" onClick={sair}>
        Sair
      </Button>
    </>
  );

  // Celular: os controles cabem num painel aberto por um botão compacto (senão a barra força a página a ~550px)
  if (telaPequena) {
    return (
      <>
        <Button
          color="inherit"
          size="small"
          onClick={(e) => setAncora(e.currentTarget)}
          startIcon={<AccountCircleIcon />}
          sx={{ ml: 1, minWidth: 0, whiteSpace: 'nowrap', textTransform: 'none' }}
          aria-label="Usuário, empresa e filial"
        >
          {usuario?.login || ''} · {empresaId || '—'}/{filialId || '—'}
        </Button>
        <Popover
          open={Boolean(ancora)}
          anchorEl={ancora}
          onClose={() => setAncora(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Stack spacing={1.5} sx={{ p: 2, width: 260, '& .MuiFormControl-root': { width: '100%' } }}>
            <Typography variant="subtitle2">{usuario?.login || ''}</Typography>
            {controles}
          </Stack>
        </Popover>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        ml: 2,
        // O3 (P-A2): sobre a barra azul, rótulos, valores e bordas em branco (o cinza padrão não tinha contraste)
        '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': { color: '#fff' },
        '& .MuiSelect-select, & .MuiSelect-icon': { color: '#fff' },
        '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.85)' },
      }}
    >
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {usuario?.login || ''}
      </Typography>
      {controles}
    </Box>
  );
};

export default SeletorContexto;
